import React, {
    useState,
    useEffect,
    useRef,
    useCallback,
    useMemo,
  } from "react";
  import { ringIcon } from "./icons";
  import { CHAT_BACKDROP_Z_INDEX, CHAT_FAB_Z_INDEX, CHAT_PANEL_Z_INDEX } from "@utils/uiZIndex";
  
  interface MessageContent {
    type: "widget" | "image" | "document";
    widget?: {
      widgetId: string;
    } & Record<string, any>;
    text?: string;
    uri?: string;
  }
  
  interface AckoAssistMessage {
    conversationId?: string;
    senderId?: string;
    content: Array<MessageContent>;
    replyTo?: string;
    caption?: string;
    status?: "sent" | "delivered" | "read" | "failed";
    meta?: Record<string, any>;
    role?: "user" | "system" | "assistant";
  }
  
  interface CopilotProps {
    url: string;
    fabHide: boolean;
    right?: number;
    bottom?: number;
    onEvent: (
      event:
        | "openCopilot"
        | "closeCopilot"
        | "message"
        | "connection-initialised-completely"
        | "join-conversation"
        | "summary"
        | "login"
        | "feedbackCopilot"
        | "agent",
      payload: any
    ) => void;
    zIndex?: number;
  }
  
  export const CopilotWithoutTailwind = ({
    url,
    fabHide,
    onEvent,
    right = 16,
    bottom = 150,
    zIndex = CHAT_FAB_Z_INDEX,
  }: CopilotProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [conversationJoined, setConversationJoined] = useState(false);
  
    const fabDisplayText = url.includes("bike")? "Chat with us" : "Chat or call";
    const [hasInteracted, setHasInteracted] = useState(false);
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const fabLabelRef = useRef<HTMLSpanElement>(null);
   
    const completelyLoaded = useMemo(() => {
      return conversationJoined;
    }, [conversationJoined]);
  
    useEffect(() => {
      const mediaQuery = window.matchMedia("(max-width: 650px)");
      setIsMobile(mediaQuery.matches);
      const handleResize = () => setIsMobile(mediaQuery.matches);
      mediaQuery.addEventListener("change", handleResize);
      return () => mediaQuery.removeEventListener("change", handleResize);
    }, []);
  
    useEffect(() => {
      if (isMobile && isOpen) {
        const originalScrollY = window.scrollY;
        const originalOverflow = document.body.style.overflow;
        document.body.style.position = "fixed";
        document.body.style.top = `-${originalScrollY}px`;
        document.body.style.width = "100%";
        document.body.style.overflow = "hidden";
        return () => {
          document.body.style.position = "";
          document.body.style.top = "";
          document.body.style.width = "";
          document.body.style.overflow = originalOverflow;
          window.scrollTo(0, originalScrollY);
        };
      }
    }, [isMobile, isOpen]);
  
    const handleToggleChat = () => {
      const newState = !isOpen;
      setIsOpen(newState);
      if (!hasInteracted) {
        setHasInteracted(true);
      }
      if (newState) {
        sendMessageToIframe({
          type: "copilotOpened",
          payload: { source: "fab" },
        });
      } else {
        sendMessageToIframe({
          type: "copilotClosed",
          payload: { source: "fab" },
        });
      }
    };
  
    const sendMessageToIframe = (message: { type: string; payload: any }) => {
      if (iframeRef.current && iframeRef.current.contentWindow) {
        iframeRef.current.contentWindow.postMessage(message, "*");
      }
    };
  
    const sendMessage = useCallback((messageContent: string) => {
      if (!messageContent.trim()) return;
      const fullMessage: AckoAssistMessage = {
        content: [
          {
            type: "widget",
            widget: {
              widgetId: "text",
              content: messageContent,
            },
          },
        ],
        status: "sent",
        role: "user",
        meta: {
          timestamp: new Date().toISOString(),
          source: "copilot",
        },
      };
      if (iframeRef.current && iframeRef.current.contentWindow) {
        iframeRef.current.contentWindow.postMessage(
          {
            type: "sendMessage",
            payload: {
              message: fullMessage,
              messageContent: messageContent,
              timestamp: new Date().toISOString(),
            },
          },
          "*"
        );
      }
    }, []);
  
    const setContext = (context: { type: string; payload: any }) => {
      if (setContext && typeof setContext === "function") {
        if (iframeRef.current && iframeRef.current.contentWindow) {
          iframeRef.current.contentWindow.postMessage(
            {
              type: "setContext",
              payload: {
                context: context,
              },
            },
            "*"
          );
        }
      }
    };
  
    const getSummary = () => {
      if (iframeRef.current && iframeRef.current.contentWindow) {
        iframeRef.current.contentWindow.postMessage({ type: "get-summary" }, "*");
      }
    };
  
    const open = () => {
      setIsOpen(true);
      onEvent("openCopilot", { message: "open" });
      sendMessageToIframe({
        type: "copilotOpened",
        payload: {
          source: "manual",
        },
      });
    };
    const close = () => {
      setIsOpen(false);
      onEvent("closeCopilot", { message: "close" });
      sendMessageToIframe({
        type: "copilotClosed",
        payload: {
          source: "manual",
        },
      });
    };
  
    useEffect(() => {
      if (!(window as any).copilot) {
        (window as any).copilot = {};
      }
      (window as any).copilot.sendMessage = sendMessage;
      (window as any).copilot.setContext = setContext;
      (window as any).copilot.open = open;
      (window as any).copilot.close = close;
      (window as any).copilot.getSummary = getSummary;
      return () => {
        delete (window as any).copilot.sendMessage;
        delete (window as any).copilot.setContext;
        delete (window as any).copilot.open;
        delete (window as any).copilot.close;
        delete (window as any).copilot.getSummary;
      };
    }, [sendMessage, setContext]);
  
    useEffect(() => {
      const handleMessage = (event: MessageEvent) => {
        const { type, payload } = event.data;
        console.log({ type, payload });
        switch (type) {
          case "closeCopilot":
            if (isOpen) {
              handleToggleChat();
              onEvent("closeCopilot", payload);
            }
            break;
          case "openCopilot":
            if (!isOpen) {
              handleToggleChat();
              onEvent("openCopilot", payload);
            }
            break;
          case "connection-initialised-completely":
            onEvent("connection-initialised-completely", payload);
            break;
          case "join-conversation":
            setConversationJoined(true);
            onEvent("join-conversation", payload);
            break;
          case "summary":
            onEvent("summary", payload);
            break;
          case "message":
            onEvent("message", payload);
            break;
          case "login":
            onEvent("login", payload);
            break;
          case "agent":
            onEvent("agent", payload);
            break;
          case "feedbackCopilot":
            onEvent("feedbackCopilot", payload);
            break;
  
          default:
            onEvent(type, payload);
            console.log("Received unhandled message from iframe:", event.data);
            break;
        }
      };
      window.addEventListener("message", handleMessage);
      return () => {
        window.removeEventListener("message", handleMessage);
      };
    }, [isOpen]);
  
    const handleBackdropClick = (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        handleToggleChat();
      }
    };
  
    const getBackdropClassName = () => {
      let className = "copilot-backdrop";
      if (isOpen) {
        className += " copilot-backdrop--visible";
      }
      return className;
    };
  
    const getChatWindowClassName = () => {
      let className = "copilot-chat-window";
      if (isMobile) {
        className += " copilot-chat-window--mobile";
        if (isOpen) {
          className += " copilot-chat-window--open";
        } else if (hasInteracted) {
          className += " copilot-chat-window--closing";
        } else {
          className += " copilot-chat-window--hidden";
        }
      } else {
        className += " copilot-chat-window--desktop";
        if (isOpen) {
          className += " copilot-chat-window--open";
        } else if (hasInteracted) {
          className += " copilot-chat-window--closing";
        } else {
          className += " copilot-chat-window--hidden";
        }
      }
      return className;
    };
  
    const getFabWrapperClassName = () => {
      let className = "copilot-fab-wrapper";
      if (!completelyLoaded) {
        className += " copilot-fab-wrapper--loading";
      }
      return className;
    };
  
    const getFabButtonClassName = () => {
      let className = "copilot-fab-button";
      if (!completelyLoaded) {
        className += " copilot-fab-button--loading";
      }
      return className;
    };
  
    return (
      <div className="acko-copilot-container">
        {isMobile && (
          <div className={getBackdropClassName()} onClick={handleBackdropClick} />
        )}
  
        <div
          className={getChatWindowClassName()}
          style={{
            width: isMobile ? "100%" : "375px",
            height: isMobile
              ? "calc(100svh - env(safe-area-inset-bottom))"
              : "70vh",
            bottom: isMobile ? "auto" : bottom,
            right: isMobile ? "0px" : right,
            boxShadow: "0 10px 25px rgba(64,49,91,0.54)",
          }}
        >
          <iframe
            ref={iframeRef}
            src={url}
            className="copilot-iframe"
            referrerPolicy="origin"
            onLoad={() => {}}
            title="Acko Copilot Chat"
          />
        </div>
  
        {!fabHide && !isOpen && (
          <div
            className={getFabWrapperClassName()}
            style={{ right, bottom, zIndex }}
          >
            {completelyLoaded && (
              <span
                className="copilot-notification-dot"
                style={{ zIndex: zIndex + 1 }}
              >
                <span className="copilot-notification-dot__ping"></span>
                <span className="copilot-notification-dot__solid"></span>
              </span>
            )}
  
            <button
              onClick={handleToggleChat}
              className={getFabButtonClassName()}
              style={{
                minWidth: "50px",
                height: "50px",
                borderRadius: "24px",
                overflow: "hidden",
                padding: "4px 10px",
              }}
              aria-label={isOpen ? "Close chat" : "Open chat"}
            >
              {fabDisplayText && (
                <span
                  ref={fabLabelRef}
                  className="copilot-fab-label"
                  style={{ fontSize: "14px", marginLeft: "8px" }}
                >
                  {fabDisplayText}
                </span>
              )}
  
              <div className="copilot-fab-icon">
                <img
                  src={`data:image/png;base64,${ringIcon}`}
                  alt="Chat"
                  style={{ width: "24px", height: "24px" }}
                />
              </div>
            </button>
          </div>
        )}
  
        <style>
          {`
            .acko-copilot-container {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
            }
  
            /* Backdrop */
            .copilot-backdrop {
              position: fixed;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              background-color: #161a2652;
              backdrop-filter: blur(16px);
              -webkit-backdrop-filter: blur(16px);
              z-index: ${CHAT_BACKDROP_Z_INDEX};
              transition: opacity 200ms ease;
              opacity: 0;
              pointer-events: none;
            }
  
            .copilot-backdrop--visible {
              opacity: 1;
              pointer-events: auto;
            }
  
            /* Chat Window */
            .copilot-chat-window {
              position: fixed;
              overflow: hidden;
              z-index: ${CHAT_PANEL_Z_INDEX};
            }
  
            .copilot-chat-window--mobile {
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
            }
  
            .copilot-chat-window--desktop {
              right: 16px;
              border-radius: 1.5rem;
            }
  
            .copilot-chat-window--open {
              pointer-events: auto;
              animation: copilot-chat-slide-up 0.5s ease-in-out forwards;
            }
  
            .copilot-chat-window--closing {
              pointer-events: none;
              animation: copilot-chat-slide-down 0.5s ease-in-out forwards;
            }
  
            .copilot-chat-window--hidden {
              pointer-events: none;
              transform: translateY(100%);
              opacity: 0;
            }
  
            /* Iframe */
            .copilot-iframe {
              width: 100%;
              height: 100%;
              border: none;
            }
  
            /* FAB Wrapper */
            .copilot-fab-wrapper {
              position: fixed;
              border-radius: 9999px;
            }
  
            .copilot-fab-wrapper--loading {
              opacity: 0.7;
            }
  
            /* FAB Button */
            .copilot-fab-button {
              position: relative;
              display: flex;
              padding-left: 10px;
              padding-right: 10px;
              background: linear-gradient(to right, #fff0f7, #ebe8ff);
              align-items: center;
              box-shadow: 0 6px 12px #7300ff24;
              border: none;
              border-bottom: 1px solid #b191ed;
              cursor: pointer;
            }
  
            .copilot-fab-button:focus {
              outline: none;
            }
  
            .copilot-fab-button:active {
              border: none;
            }
  
            .copilot-fab-button--loading {
              filter: grayscale(1);
              box-shadow: none;
              border: none;
              cursor: default;
            }
  
            /* FAB Label */
            .copilot-fab-label {
              white-space: nowrap;
              color: #1e293b;
            }
  
            /* FAB Icon */
            .copilot-fab-icon {
              margin-left: 8px;
            }
  
            /* Notification Dot */
            .copilot-notification-dot {
              position: absolute;
              display: flex;
              height: 0.5rem;
              width: 0.5rem;
              left: 0;
              top: 5px;
            }
  
            .copilot-notification-dot__ping {
              animation: copilot-ping 1s cubic-bezier(0, 0, 0.2, 1) infinite;
              position: absolute;
              display: inline-flex;
              height: 100%;
              width: 100%;
              border-radius: 9999px;
              background-color: #f87171;
              opacity: 0.75;
            }
  
            .copilot-notification-dot__solid {
              position: relative;
              display: inline-flex;
              border-radius: 9999px;
              height: 0.5rem;
              width: 0.5rem;
              background-color: #ef4444;
            }
  
            /* Animations */
            @keyframes copilot-ping {
              75%, 100% {
                transform: scale(2);
                opacity: 0;
              }
            }
  
            @keyframes copilot-appear-opacity {
              0% {
                opacity: 0;
              }
              100% {
                opacity: 1;
              }
            }
            
            @keyframes copilot-chat-slide-up {
              0% {
                transform: translateY(100%);
                opacity: 0;
              }
              20% {
                transform: translateY(30%);
                opacity: 0;
              }
              100% {
                transform: translateY(0%);
                opacity: 1;
              }
            }
            
            @keyframes copilot-chat-slide-down {
              0% {
                transform: translateY(0%);
                opacity: 1;
              }
              30% {
                transform: translateY(60%);
                opacity: 0;
              }
              100% {
                transform: translateY(100%);
                opacity: 0;
              }
            }
            
            .copilot-animate-appear-message {
              animation: copilot-appear-opacity 1.5s ease-in-out 1.5s forwards;
            }
            
            .copilot-animate-appear-opacity {
              animation: copilot-appear-opacity 1s ease-in-out forwards;
            }
            
            .copilot-animation-delay-500 {
              animation-delay: 0.5s;
            }
          `}
        </style>
      </div>
    );
  };
  